import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';
import { useDispatch, useSelector } from 'react-redux';
import { selectGroupGuid, selectIncident } from 'slices/commonSelectors';
import {
  addMemberToTeams,
  RemoveUserFromWorkspaceTeamSite,
} from 'actions/teamsActions';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useFetchIncidentWorkspaceTeamStatus } from '../useFetchIncidentWorkspaceTeamStatus';
import { workLogKeys } from './useWorkLog';
import { useSearchParams } from 'react-router';

import { incidentRosterKeys } from '../../components/IAP/hooks/incidentRoster/incidentRoster.queryKeys';
import { keys as incidentPositionKeys } from '../../components/IncidentConfig/hooks/useGetIncidentPositionInfo';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import { filterRosterCatalogForFemaOps } from 'components/IncidentConfig/roster/templates/rosterTemplateCatalog';
import {
  useFemaOpsBriefingFlag,
  useHwcgFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import {
  getUscgFocRosterCatalogEntries,
  isUscgFocRosterTemplateSlug,
  USCG_FOC_DEMO_APPLY_MESSAGE,
} from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/uscgFocPlanning';

// Query keys
export const keys = {
  incidentRoster: 'incidentRoster',
  /** Shared with hooks/useHwcgActivationLog so seating refreshes the log. */
  hwcgActivationLog: 'hwcgActivationLog',
  groupRosters: 'groupRosters',
  incidentSchedule: 'incidentSchedule',
  userIncidentSchedule: 'userIncidentSchedule',
  incidentPositions: 'incidentPositions',
  seatResources: 'seatResources',
  rosterTemplates: 'rosterTemplates',
};

// Types
interface RootState {
  app: {
    user: {
      user_guid: string;
    };
  };
}

export interface IncidentPosition {
  is_workgroup: boolean;
  id: string;
  item_id: string;
  name: string;
  parent_id: string | null;
  parent_name: string | null;
  is_template: boolean;
  isResourcePosition: boolean;
  resourceSetId: string | null;
  /**
   * Kind of the attached resource ("Equipment", "Vehicle", ...); null for
   * personnel seats.
   */
  resourceType: string | null;
  pointOfContactUserId: string | null;
  /** Flagged to leave the roster when the operational period next advances. */
  retire_next_op: boolean;
  /**
   * ICS organisational element this seat represents ("Section", "Branch", ...),
   * or "None". Distinct from resourceType, which describes an attached resource
   * rather than the seat.
   */
  category: string;
  /** The user's own label, set only when category is "Custom". */
  custom_category: string | null;
  /**
   * Where this seat has been dragged to on the org chart. Null means it has
   * never been placed by hand, so the automatic dagre layout decides. The pair
   * is always both null or both set.
   */
  layout_x: number | null;
  layout_y: number | null;
}

interface IncidentMember {
  user_guid: string;
  email_address?: string;
  incident_member_id?: string;
  seat?: string;
  is_leader?: boolean;
  [key: string]: any;
}

interface AddUserToIncidentRosterParams {
  positionAssignments: {
    positionId: string;
    positionName: string;
    incidentMemberUserId: string;
    isLeader: boolean;
    designation?: 'primary' | 'secondary' | 'deputy' | 'assistant' | null;
  }[];
  isDraft?: boolean;
}

interface AssignMemberToPositionInRosterParams {
  incidentMemberId: string;
  positionId: string;
  incidentId: string;
  groupId: string;
  isLeader?: boolean;
  isDraft?: boolean;
}

interface UpdatePositionAssignmentParams {
  member: IncidentMember;
  incidentMemberId: string;
  positionId: string;
  isLeader?: boolean;
  designation?: 'primary' | 'secondary' | 'deputy' | 'assistant' | null;
  isDraft?: boolean;
}

interface MarkIncidentUserActiveParams {
  user_guid: string;
  incident_member_id: string;
}

interface AddNonMemberToIncidentParams {
  incidentNonMember: any;
  incident_id: string;
}

export interface TeamMembership {
  name: string;
}

export interface ProfileSettings {
  is_new_user: string;
  userName: string;
}

export interface GroupRosterItem {
  user_guid: string;
  email_address: string;
  subscription_id: string;
  currently_selected: any;
  profile_settings: ProfileSettings;
  contact_methods: ContactMethod[];
  user_name: string;
  group_guid: string;
  group_name: string;
  is_closed: boolean | null;
  default_settings: any;
  default_sender_email: null | string;
  risk_levels: string[];
  daily_digest_team: string;
  allow_admin_profile_edits: boolean;
  role_assignments: { name: string }[];
  assigned_incidents: {
    incident_id: string;
    incident_name: string;
    position_name: string;
  }[];
  unavailability: any[];
  home_aor: string | null;
}

/**
 * Draft fallbacks keep the live query object but pin a success shape TanStack
 * Query's union allows.
 */
function asDraftQuerySuccess<TData>(
  query: UseQueryResult<TData, Error>,
  data: TData
): UseQueryResult<TData, Error> {
  return {
    ...query,
    data,
    isLoading: false,
    isPending: false,
    isFetching: false,
    isSuccess: true,
    isError: false,
    isLoadingError: false,
    isRefetchError: false,
    error: null,
    status: 'success',
  } as unknown as UseQueryResult<TData, Error>;
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useGetRosterForIncident = (
  incidentId?: string
): UseQueryResult<RosterEntry[], Error> => {
  const draft = useOptionalCreateRosterDraft();
  const actualIncidentId = useActualIncidentId();
  const resolvedIncidentId = incidentId || actualIncidentId;

  const query = useQuery<RosterEntry[], Error>({
    queryKey: [keys.incidentRoster, resolvedIncidentId],
    queryFn: async () => {
      if (!resolvedIncidentId) return [];
      const response = await axios(config.apiGateway.getRosterForIncident, {
        method: 'POST',
        data: {
          incident_id: resolvedIncidentId,
        },
      });
      return response.data || [];
    },
    enabled: !draft && !!resolvedIncidentId,
  });

  if (draft) {
    return asDraftQuerySuccess(query, draft.members);
  }

  return query;
};

export const useGetRostersForGroup = (
  groupGuid: string
): UseQueryResult<GroupRosterItem[], Error> => {
  const { user_guid } = useSelector((state: RootState) => state.app.user);

  return useQuery<GroupRosterItem[], Error>({
    queryKey: [keys.groupRosters, groupGuid],
    queryFn: async () => {
      if (!groupGuid) return [];
      const response = await axios(config.apiGateway.fetchRostersForGroups, {
        method: 'POST',
        data: {
          group_guids: [groupGuid],
          user_guid: user_guid,
        },
      });
      return response.data || [];
    },
    enabled: !!groupGuid && !!user_guid,
  });
};

/**
 * Callable form for flows that only learn the incident id at call time, such as
 * seating people right after creating the incident.
 */
export const addUsersToIncidentRoster = async ({
  incidentId,
  groupGuid,
  positionAssignments,
  isDraft = false,
  hwcgActivationNotice = false,
}: {
  incidentId: string;
  groupGuid: string;
  /** HWCG workspaces: send each seated user the two-step activation notice. */
  hwcgActivationNotice?: boolean;
} & AddUserToIncidentRosterParams) => {
  const response = await axios(
    config.apiGateway.addMemberToIncidentRoster(incidentId),
    {
      method: 'POST',
      data: {
        positionAssignments,
        isDraft,
        groupId: groupGuid,
        hwcgActivationNotice,
      },
    }
  );
  return response.data;
};

export const useAddUsersToIncidentRoster = (
  incidentId: string,
  groupGuid: string
) => {
  const draft = useOptionalCreateRosterDraft();
  const queryClient = useQueryClient();
  const { data: teamsStatus } = useFetchIncidentWorkspaceTeamStatus();
  const { data: groupRoster = [] } = useGetRostersForGroup(groupGuid);
  const [searchParams] = useSearchParams();
  const urlIsDraft = searchParams.get('mode') === 'draft';
  const dispatch = useDispatch();
  const hwcg = useHwcgFlag();

  return useMutation({
    mutationFn: async ({
      positionAssignments,
      isDraft,
    }: AddUserToIncidentRosterParams) => {
      if (draft) {
        draft.addMembers(
          positionAssignments.map((assignment) => {
            const person = groupRoster.find(
              (entry) => entry.user_guid === assignment.incidentMemberUserId
            );
            return {
              positionId: assignment.positionId,
              user_guid: assignment.incidentMemberUserId,
              name: person?.user_name || assignment.incidentMemberUserId,
              email_address: person?.email_address || '',
              isLeader: assignment.isLeader,
            };
          })
        );
        return {};
      }
      return addUsersToIncidentRoster({
        incidentId,
        groupGuid,
        positionAssignments,
        isDraft: isDraft ?? urlIsDraft,
        hwcgActivationNotice: hwcg,
      });
    },
    onSuccess: (_response, variables) => {
      if (draft) {
        toast.success('User added to incident successfully');
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      if (hwcg) {
        queryClient.invalidateQueries({
          queryKey: [keys.hwcgActivationLog, incidentId],
        });
      }
      toast.success('User added to incident successfully');
      if (teamsStatus === 'done') {
        dispatch(
          addMemberToTeams(
            incidentId,
            variables.positionAssignments.map(
              (assignment) => assignment.incidentMemberUserId
            )
          )
        );
      }
    },
    onError: (error: any) => {
      if (error.response?.data === '{"ERROR":"User does not exist"}') {
        toast.error('User does not exist');
      } else if (
        error.response?.data === '{"ERROR":"User already in this Incident"}'
      ) {
        toast.error('User already in this Incident');
      } else {
        handleError(error, 'ERROR adding incident member');
      }
    },
  });
};

export const useAssignMemberToPositionInRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlIsDraft = searchParams.get('mode') === 'draft';
  const hwcg = useHwcgFlag();

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      positionId,
      isLeader,
      isDraft,
    }: AssignMemberToPositionInRosterParams) => {
      const effectiveIsDraft = isDraft ?? urlIsDraft;
      const response = await axios(
        config.apiGateway.assignMemberToPositionInRoster(incidentId),
        {
          method: 'POST',
          data: {
            incidentMemberId,
            positionId,
            isLeader,
            isDraft: effectiveIsDraft,
            groupId,
            hwcgActivationNotice: hwcg,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      if (hwcg) {
        queryClient.invalidateQueries({
          queryKey: [keys.hwcgActivationLog, incidentId],
        });
      }
      toast.success('Member assigned to position successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR assigning member to position in roster');
    },
  });
};

interface UpdateMemberPositionsInRosterParams {
  incidentMemberId: string;
  newPositions: {
    positionId: string;
    isLeader: boolean;
  }[];
  isDraft?: boolean;
}

export const useUpdateMemberPositionsInRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();
  const urlIsDraft = searchParams.get('mode') === 'draft';
  const hwcg = useHwcgFlag();

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      newPositions,
      isDraft,
    }: UpdateMemberPositionsInRosterParams) => {
      const effectiveIsDraft = isDraft ?? urlIsDraft;
      const response = await axios(
        config.apiGateway.updateMemberPositionsInRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            incidentId,
            incidentMemberId,
            newPositions,
            isDraft: effectiveIsDraft,
            hwcgActivationNotice: hwcg,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      if (hwcg) {
        queryClient.invalidateQueries({
          queryKey: [keys.hwcgActivationLog, incidentId],
        });
      }
      toast.success('Member positions updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to update member positions in roster');
    },
  });
};

interface RemoveMemberFromRosterParams {
  user: IncidentMember;
  isDraft?: boolean;
}

export const useRemoveMemberFromIncidentRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();
  const { teams_id: teamsId } = useSelector(selectIncident);
  const [searchParams] = useSearchParams();
  const urlIsDraft = searchParams.get('mode') === 'draft';
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ user, isDraft }: RemoveMemberFromRosterParams) => {
      const effectiveIsDraft = isDraft ?? urlIsDraft;
      const response = await axios(
        config.apiGateway.removeMemberFromIncidentRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            incidentMemberId: user.incident_member_id,
            isDraft: effectiveIsDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_response, { user }) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('User removed from incident successfully');
      if (teamsId) {
        dispatch(
          RemoveUserFromWorkspaceTeamSite(
            incidentId,
            user.email_address,
            'incident'
          )
        );
      }
    },
    onError: (error: any) => {
      handleError(error, 'Failed to remove member from incident roster');
    },
  });
};

interface RemoveMemberFromRosterPositionParams {
  incidentMemberId: string;
  positionId: string;
  isDraft?: boolean;
}

export const useRemoveMemberFromPositionInRoster = (
  incidentId: string,
  groupId: string
) => {
  const draft = useOptionalCreateRosterDraft();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlIsDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      positionId,
      isDraft,
    }: RemoveMemberFromRosterPositionParams) => {
      if (draft) {
        draft.removeMemberFromPosition(incidentMemberId, positionId);
        return {};
      }
      const effectiveIsDraft = isDraft ?? urlIsDraft;
      const response = await axios(
        config.apiGateway.removeMemberFromPositionInRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            incidentMemberId,
            positionId,
            isDraft: effectiveIsDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      if (draft) {
        toast.success('Member removed from position successfully');
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Member removed from position successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR removing member from position in roster');
    },
  });
};

interface RemoveSelfFromRosterParams {
  user: IncidentMember;
  isDraft?: boolean;
}

export const useRemoveSelfFromRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();
  const { teams_id: teamsId } = useSelector(selectIncident);
  const [searchParams] = useSearchParams();
  const urlIsDraft = searchParams.get('mode') === 'draft';
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ isDraft }: RemoveSelfFromRosterParams) => {
      const effectiveIsDraft = isDraft ?? urlIsDraft;
      const response = await axios(
        config.apiGateway.removeSelfFromRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            isDraft: effectiveIsDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_response, { user }) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Successfully left the incident');
      if (teamsId) {
        dispatch(
          RemoveUserFromWorkspaceTeamSite(
            incidentId,
            user.email_address,
            'incident'
          )
        );
      }
    },
    onError: (error: any) => {
      handleError(error, 'removeSelfFromIncidentError');
    },
  });
};

export const useMarkIncidentUserActive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ user_guid }: MarkIncidentUserActiveParams) => {
      const response = await axios(config.apiGateway.markIncidentMemberActive, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          user_guid,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: workLogKeys.byIncident(incidentId),
      });
      toast.success('User marked as active');
    },
    onError: (error: any) => {
      if (
        error.response?.data?.ERROR?.includes(
          'You can not work on two different incident at the same time.'
        )
      ) {
        toast.error(
          'User is already working on another incident. You can not work on two different incidents at the same time.',
          { autoClose: 10000 } as ToastOptions
        );
      } else {
        handleError(error, 'Failed to activate incident user');
      }
    },
  });
};

export const useMarkIncidentUserInactive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      user_guid,
      logout_time,
      minutes_worked,
      incident_member_id,
    }: {
      user_guid: string;
      logout_time: Date;
      minutes_worked: number;
      incident_member_id: string;
    }) => {
      const response = await axios(
        config.apiGateway.markIncidentMemberDeactive,
        {
          method: 'POST',
          data: {
            incident_id: incidentId,
            user_guid,
            logout_time,
            minutes_worked,
            group_guid: groupGuid,
            incident_member_id,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [keys.incidentRoster, incidentId],
        (oldData: any) => {
          if (!oldData) return undefined;
          return oldData.map((member: any) => {
            if (member.incident_member_id === variables.incident_member_id) {
              return {
                ...member,
                active: 0,
                active_time: null,
              };
            }
            return member;
          });
        }
      );

      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });

      queryClient.invalidateQueries({
        queryKey: workLogKeys.byIncident(incidentId),
      });

      toast.success('User marked as inactive');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to deactivate incident user');
    },
  });
};

export const useUpdatePositionAssignment = () => {
  const queryClient = useQueryClient();

  const incidentId = useActualIncidentId();
  const [searchParams] = useSearchParams();
  const urlIsDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      positionId,
      isLeader,
      designation,
      isDraft,
    }: UpdatePositionAssignmentParams) => {
      const effectiveIsDraft = isDraft ?? urlIsDraft;
      const response = await axios(
        config.apiGateway.updatePositionAssignment(incidentId),
        {
          method: 'POST',
          data: {
            incidentId,
            incidentMemberId,
            positionId,
            isLeader,
            designation,
            draft: effectiveIsDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [keys.incidentSchedule, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          keys.userIncidentSchedule,
          incidentId,
          variables.member.user_guid,
          variables.incidentMemberId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Incident member updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to edit incident member');
    },
  });
};

export const useAddNonMemberToIncident = (groupGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentNonMember,
      incident_id,
    }: AddNonMemberToIncidentParams) => {
      const response = await axios(config.apiGateway.addNonMemberToIncident, {
        method: 'POST',
        data: {
          incident_id,
          incidentNonMember,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, variables.incident_id],
      });
      toast.success('Non-member added to incident successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to add non-member to incident');
    },
  });
};

interface ToggleApprovalAuthorityParams {
  incident_id: string;
  target_user_guid: string;
  approval_authority: boolean;
}

export const useToggleApprovalAuthority = (
  incident_id: string,
  _group_guid?: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      target_user_guid,
      approval_authority,
    }: Omit<ToggleApprovalAuthorityParams, 'incident_id'>) => {
      const response = await axios(config.apiGateway.toggleApprovalAuthority, {
        method: 'POST',
        data: {
          incident_id,
          target_user_guid,
          approval_authority,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incident_id],
      });
      toast.success('Approval authority updated successfully');
    },
    onError: (error: any) => {
      const errorMessage = parseErrorMessage(error);
      if (errorMessage.includes('last remaining user')) {
        toast.error(
          'Cannot remove approval authority from the last remaining user'
        );
      } else if (errorMessage.includes('do not have approval authority')) {
        toast.error(
          'You do not have approval authority to perform this action'
        );
      } else {
        handleError(error, 'Failed to update approval authority');
      }
    },
  });
};

export const getPositionsForIncident = async (incidentId: string) => {
  const response = await axios.get<IncidentPosition[]>(
    `${config.apiGateway.incidentPositions}/${incidentId}`,
    { params: { _: Date.now() } }
  );
  return response.data || [];
};

export const useGetPositionsForIncident = () => {
  const draft = useOptionalCreateRosterDraft();
  const resolvedIncidentId = useActualIncidentId();

  const query = useQuery({
    queryKey: [keys.incidentPositions, resolvedIncidentId],
    queryFn: () => getPositionsForIncident(resolvedIncidentId),
    enabled: !draft && !!resolvedIncidentId,
  });

  if (draft) {
    return asDraftQuerySuccess(query, draft.positions);
  }

  return query;
};

export const useGetPositionsForIncidentId = (incidentId: string) => {
  return useQuery({
    queryKey: [keys.incidentPositions, incidentId],
    queryFn: () => getPositionsForIncident(incidentId),
    enabled: !!incidentId,
  });
};

export const useCreateNewPositionForIncident = () => {
  const draft = useOptionalCreateRosterDraft();
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      name,
      reportsTo,
      isWorkgroup = false,
    }: {
      name: string;
      reportsTo?: string;
      isWorkgroup?: boolean;
    }) => {
      if (draft) {
        draft.createSeat({ name, reportsTo, isWorkgroup });
        return {};
      }
      const response = await axios.post(config.apiGateway.incidentPositions, {
        incident_id: incidentId,
        name,
        reports_to: reportsTo,
        is_workgroup: isWorkgroup,
      });

      return response.data;
    },
    onSuccess: () => {
      if (draft) {
        toast.success('Position created successfully');
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      toast.success('Position created successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to create new position');
    },
  });
};

export const useEditPositionForIncident = () => {
  const draft = useOptionalCreateRosterDraft();
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      reportsTo,
      isWorkgroup = false,
    }: {
      id: string;
      name: string;
      reportsTo?: string | null;
      isWorkgroup?: boolean;
    }) => {
      if (draft) {
        draft.editSeat({ id, name, reportsTo, isWorkgroup });
        return {};
      }
      const response = await axios.put(
        `${config.apiGateway.incidentPositions}/${id}`,
        {
          incident_id: incidentId,
          name,
          reports_to: reportsTo ?? null,
          is_workgroup: isWorkgroup,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      if (draft) {
        toast.success('Position updated successfully');
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('Position updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to edit position');
    },
  });
};

export const useDeletePositionForIncident = () => {
  const draft = useOptionalCreateRosterDraft();
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      if (draft) {
        draft.deleteSeat(id);
        return {};
      }
      const response = await axios.delete(
        `${config.apiGateway.incidentPositions}/${id}`,
        {
          data: {
            incident_id: incidentId,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      if (draft) {
        toast.success('Position deleted successfully');
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('Position deleted successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to delete position');
    },
  });
};

// ─── Resource roster hooks ────────────────────────────────────────────────────

interface AttachResourceToRosterParams {
  incidentId: string;
  resourceSetId: string;
  pointOfContactUserId?: string | null;
  reportsToPositionId?: string | null;
  isDraft?: boolean;
}

export const useAttachResourceToRoster = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      incidentId: targetIncidentId,
      resourceSetId,
      pointOfContactUserId,
      reportsToPositionId,
      isDraft,
    }: AttachResourceToRosterParams) => {
      const response = await axios.post(
        config.apiGateway.attachResourceToRoster,
        {
          incidentId: targetIncidentId,
          resourceSetId,
          pointOfContactUserId,
          reportsToPositionId,
          isDraft,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
    },
    onError: (error: any) => {
      handleError(error, 'Failed to attach resource to roster');
    },
  });
};

interface DetachResourceFromRosterParams {
  positionId: string;
}

export const useDetachResourceFromRoster = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({ positionId }: DetachResourceFromRosterParams) => {
      const response = await axios.delete(
        config.apiGateway.detachResourceFromRoster(positionId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
    },
    onError: (error: any) => {
      handleError(error, 'Failed to detach resource from roster');
    },
  });
};

interface UpdateRosterResourceParams {
  positionId: string;
  pointOfContactUserId?: string | null;
  reportsToPositionId?: string | null;
}

export const useUpdateRosterResource = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      positionId,
      pointOfContactUserId,
      reportsToPositionId,
    }: UpdateRosterResourceParams) => {
      const response = await axios.patch(
        config.apiGateway.updateRosterResource(positionId),
        {
          pointOfContactUserId,
          reportsToPositionId,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
    },
    onError: (error: any) => {
      handleError(error, 'Failed to update roster resource');
    },
  });
};

// ─── Seat resource hooks ──────────────────────────────────────────────────────
// A seat resource attaches an incident resource to an existing personnel seat. Unlike a resource position, which is
// incident-scoped, these belong to one roster, so the draft and active lists are fetched separately.

export interface SeatResource {
  id: string;
  rosterId: string;
  positionId: string;
  resourceSetId: string;
  resourceName: string | null;
  resourceKind: string | null;
  pointOfContactUserId: string;
}

/**
 * A draft roster has no seat resources, but the empty result still has to be
 * the same array each render or every consumer sees new data every time.
 */
const NO_DRAFT_SEAT_RESOURCES: SeatResource[] = [];

export const useGetSeatResourcesForRoster = (
  isDraft = false
): UseQueryResult<SeatResource[], Error> => {
  const draft = useOptionalCreateRosterDraft();
  const incidentId = useActualIncidentId();

  const query = useQuery<SeatResource[], Error>({
    queryKey: [keys.seatResources, incidentId, isDraft],
    queryFn: async () => {
      if (!incidentId) return [];
      const response = await axios.get(
        config.apiGateway.seatResourcesForRoster(incidentId),
        { params: { draft: String(isDraft) } }
      );
      return response.data || [];
    },
    enabled: !draft && !!incidentId,
  });

  if (draft) {
    return asDraftQuerySuccess<SeatResource[]>(query, NO_DRAFT_SEAT_RESOURCES);
  }

  return query;
};

/**
 * Both roster views are invalidated together, since promoting or editing one
 * can change what the other shows.
 */
const useInvalidateSeatResources = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [keys.seatResources, incidentId],
    });
};

interface AttachResourceToSeatParams {
  positionId: string;
  resourceSetId: string;
  pointOfContactUserId: string;
  isDraft?: boolean;
}

export const useAttachResourceToSeat = () => {
  const incidentId = useActualIncidentId();
  const invalidateSeatResources = useInvalidateSeatResources();

  return useMutation({
    mutationFn: async ({
      positionId,
      resourceSetId,
      pointOfContactUserId,
      isDraft,
    }: AttachResourceToSeatParams) => {
      const response = await axios.post(
        config.apiGateway.attachResourceToSeat(positionId),
        { incidentId, resourceSetId, pointOfContactUserId, isDraft }
      );
      return response.data;
    },
    onSuccess: () => invalidateSeatResources(),
    onError: (error: any) => {
      handleError(error, 'Failed to attach resource to seat');
    },
  });
};

interface UpdateSeatResourceParams {
  seatResourceId: string;
  pointOfContactUserId: string;
}

export const useUpdateSeatResource = () => {
  const invalidateSeatResources = useInvalidateSeatResources();

  return useMutation({
    mutationFn: async ({
      seatResourceId,
      pointOfContactUserId,
    }: UpdateSeatResourceParams) => {
      const response = await axios.patch(
        config.apiGateway.seatResource(seatResourceId),
        { pointOfContactUserId }
      );
      return response.data;
    },
    onSuccess: () => invalidateSeatResources(),
    onError: (error: any) => {
      handleError(error, 'Failed to update attached resource');
    },
  });
};

export const useDetachResourceFromSeat = () => {
  const invalidateSeatResources = useInvalidateSeatResources();

  return useMutation({
    mutationFn: async ({ seatResourceId }: { seatResourceId: string }) => {
      const response = await axios.delete(
        config.apiGateway.seatResource(seatResourceId)
      );
      return response.data;
    },
    onSuccess: () => invalidateSeatResources(),
    onError: (error: any) => {
      handleError(error, 'Failed to detach resource from seat');
    },
  });
};

interface SetRetireNextOpParams {
  positionId: string;
  retireNextOp: boolean;
}

/**
 * Flags a position to leave the roster when the operational period next
 * advances. Nothing changes on the current roster until then, and the flag can
 * be cleared right up to the advance.
 */
export const useSetRetireNextOp = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({ positionId, retireNextOp }: SetRetireNextOpParams) => {
      const response = await axios.patch(
        config.apiGateway.setRetireNextOp(positionId),
        { retireNextOp }
      );
      return response.data;
    },
    onSuccess: (_data, { positionId }) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentPositionKeys.byPosition(incidentId, positionId),
      });
    },
    onError: (error: any) => {
      handleError(error, 'Failed to update retirement setting');
    },
  });
};

interface SetPositionCategoryParams {
  positionId: string;
  category: string;
  /** Required when category is "Custom", rejected otherwise. */
  customCategory?: string | null;
}

/**
 * Sets which ICS organisational element a position represents.
 *
 * Allowed on every kind of seat, including the standard ICS templates, since
 * those are the ones most likely to be a Section or a Branch.
 */
export const useSetPositionCategory = () => {
  const draft = useOptionalCreateRosterDraft();
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      positionId,
      category,
      customCategory,
    }: SetPositionCategoryParams) => {
      if (draft) {
        draft.setSeatCategory(positionId, category, customCategory);
        return {};
      }
      const response = await axios.patch(
        config.apiGateway.setPositionCategory(positionId),
        { category, customCategory: customCategory ?? null }
      );
      return response.data;
    },
    onSuccess: (_data, { positionId }) => {
      if (draft) return;
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentPositionKeys.byPosition(incidentId, positionId),
      });
    },
    onError: (error: any) => {
      handleError(error, 'Failed to update category');
    },
  });
};

export const applyRosterTemplateToIncident = async (
  incidentId: string,
  slug: string,
  mode: 'add' | 'replace'
) => {
  const response = await axios.post(
    config.apiGateway.applyRosterTemplate(incidentId),
    { slug, mode }
  );
  return response.data as {
    slug: string;
    mode: 'add' | 'replace';
    created: { id: string; name: string }[];
    skipped: string[];
    removed: string[];
  };
};

export const attachRosterExpansionToIncident = async (
  incidentId: string,
  slug: string,
  parentPositionId: string
) => {
  const response = await axios.post(
    config.apiGateway.attachRosterExpansion(incidentId),
    { slug, parentPositionId }
  );
  return response.data as {
    slug: string;
    created: { id: string; name: string }[];
    skipped: string[];
  };
};

export const addCatalogPositionToIncident = async (
  incidentId: string,
  values: { name: string; parentPositionId: string; isWorkgroup?: boolean }
) => {
  const response = await axios.post(
    config.apiGateway.addCatalogPosition(incidentId),
    values
  );
  return response.data;
};

/**
 * The built-in template catalog, plus the org's saved templates when we know
 * which org we are in. Inside the create-incident wizard the group comes from
 * the draft, since no incident exists yet.
 */
export const useRosterTemplatesCatalog = () => {
  const draft = useOptionalCreateRosterDraft();
  const selectedGroupGuid = useSelector(selectGroupGuid);
  const groupGuid = draft?.groupGuid ?? selectedGroupGuid ?? undefined;
  const femaOps = useFemaOpsBriefingFlag();
  const uscgFocEnabled = useUscgFocRequirementsFlag();

  return useQuery({
    queryKey: [
      keys.rosterTemplates,
      groupGuid ?? '',
      femaOps,
      uscgFocEnabled ? 'uscg-foc' : '',
    ],
    queryFn: async () => {
      const response = await axios.get(config.apiGateway.rosterTemplates, {
        params: groupGuid ? { groupGuid } : undefined,
      });
      const data = response.data as {
        templates: {
          slug: string;
          name: string;
          description: string | null;
          category: string;
          attachOnly: boolean;
          positionCount: number;
          nodes?: {
            name: string;
            parentName: string | null;
            isWorkgroup?: boolean;
            category?: string;
            customCategory?: string | null;
          }[];
        }[];
        addablePositionTitles: string[];
      };
      if (!uscgFocEnabled) return data;
      const seen = new Set(data.templates.map((template) => template.slug));
      return {
        ...data,
        templates: [
          ...data.templates,
          ...getUscgFocRosterCatalogEntries().filter(
            (template) => !seen.has(template.slug)
          ),
        ],
      };
    },
    select: (data) => filterRosterCatalogForFemaOps(data, femaOps),
  });
};

export const useApplyRosterTemplate = () => {
  const draft = useOptionalCreateRosterDraft();
  const catalogQuery = useRosterTemplatesCatalog();
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      slug,
      mode,
    }: {
      slug: string;
      mode: 'add' | 'replace';
    }) => {
      if (isUscgFocRosterTemplateSlug(slug)) {
        return {
          slug,
          mode,
          created: [] as string[],
          skipped: [] as string[],
          removed: [] as string[],
          demonstration: true as const,
        };
      }
      if (draft) {
        const template = catalogQuery.data?.templates.find(
          (entry) => entry.slug === slug && !entry.attachOnly
        );
        if (!template?.nodes?.length) {
          throw new Error('That template has no seats to load.');
        }
        const created = draft.applyTemplate({
          nodes: template.nodes,
          mode,
          slug,
          name: template.name,
        });
        return {
          slug,
          mode,
          created: created.created,
          skipped: [],
          removed: [],
        };
      }
      return applyRosterTemplateToIncident(incidentId, slug, mode);
    },
    onSuccess: (data) => {
      if ('demonstration' in data && data.demonstration) {
        toast.info(USCG_FOC_DEMO_APPLY_MESSAGE);
        return;
      }
      if (!draft) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentPositions, incidentId],
        });
      }
      const created = data.created?.length ?? 0;
      if (data.mode === 'replace') {
        toast.success(
          `Replaced org chart with template (${created} seat${
            created === 1 ? '' : 's'
          })`
        );
        return;
      }
      toast.success(
        created > 0
          ? `Added ${created} seat${created === 1 ? '' : 's'} from template`
          : 'All template seats already exist on this roster'
      );
    },
    onError: (error: any) => {
      handleError(error, 'Failed to apply roster template');
    },
  });
};

export const useAttachRosterExpansion = () => {
  const draft = useOptionalCreateRosterDraft();
  const catalogQuery = useRosterTemplatesCatalog();
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      slug,
      parentPositionId,
    }: {
      slug: string;
      parentPositionId: string;
    }) => {
      if (draft) {
        const template = catalogQuery.data?.templates.find(
          (entry) => entry.slug === slug
        );
        if (!template?.nodes?.length) {
          throw new Error('That expansion has no seats to attach.');
        }
        const created = draft.attachExpansion({
          nodes: template.nodes,
          parentPositionId,
        });
        return {
          slug,
          created: created.created,
          skipped: [],
        };
      }
      return attachRosterExpansionToIncident(
        incidentId,
        slug,
        parentPositionId
      );
    },
    onSuccess: (data) => {
      if (!draft) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentPositions, incidentId],
        });
      }
      const created = data.created?.length ?? 0;
      toast.success(
        created > 0
          ? `Attached ${created} seat${created === 1 ? '' : 's'} under the selected position`
          : 'Those expansion seats already exist on this roster'
      );
    },
    onError: (error: any) => {
      handleError(error, 'Failed to attach roster expansion');
    },
  });
};

export const useAddCatalogPosition = () => {
  const draft = useOptionalCreateRosterDraft();
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      name,
      parentPositionId,
      isWorkgroup,
    }: {
      name: string;
      parentPositionId: string;
      isWorkgroup?: boolean;
    }) => {
      if (draft) {
        draft.addCatalogPosition({ name, parentPositionId, isWorkgroup });
        return {};
      }
      return addCatalogPositionToIncident(incidentId, {
        name,
        parentPositionId,
        isWorkgroup,
      });
    },
    onSuccess: () => {
      if (!draft) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentPositions, incidentId],
        });
      }
      toast.success('Position added');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to add position');
    },
  });
};
